import {useState, useEffect} from 'react';
import Cookies from 'js-cookie';
import {useGetCartQuery} from '@/services/api/cart-api';
import {getRootDomain} from '@/utils/common/url';
import {GEOBLOCKING_STATUS, GEOBLOCKING_STATUS_EVENT} from '@/services/geoblocking/geoblocking-constants';
import {COUNTRY_CODE_COOKIE, CART_ID_COOKIE, CART_ZONE_COOKIE} from '../product-constants';

function resolveFromCookies() {
  const cartId = Cookies.get(CART_ID_COOKIE);
  if (cartId) {
    const cartZone = Cookies.get(CART_ZONE_COOKIE);
    if (cartZone) return cartZone;
  }
  return Cookies.get(COUNTRY_CODE_COOKIE) || null;
}

/**
 * Resolves the user's country code using a prioritized fallback chain:
 *   1. Cart zone from RTK Query (eCommCartId present but eCartZone not yet set)
 *   2. eCartZone / omniCoutryCode cookies (page revisit or fast mount)
 *   3. window.storeVariables.countryCode (geoblocking already settled on mount)
 *   4. geoblocking:status event (component mounted before geoblocking finished)
 *
 * @returns {string|null} ISO 3166-1 alpha-2 country code, or null while resolving.
 */
export function useCountryCode() {
  const cartId = Cookies.get(CART_ID_COOKIE) || null;
  const hasCartZone = !!Cookies.get(CART_ZONE_COOKIE);

  const {data: cart} = useGetCartQuery(cartId, {
    skip: !cartId || hasCartZone,
  });

  const [countryCode, setCountryCode] = useState(
    () => globalThis.storeVariables?.countryCode || null,
  );

  // Persist eCartZone cookie when the cart API resolves — external system update only, no setState
  useEffect(() => {
    if (!cart?.zone) return;
    const domain = getRootDomain(globalThis.location.hostname);
    Cookies.set(CART_ZONE_COOKIE, cart.zone, {expires: 2000, path: '/', domain});
  }, [cart?.zone]);

  // Subscribe to geoblocking events — mirrors useSelectedZone pattern
  useEffect(() => {
    if (countryCode) return;

    // Geoblocking already settled — read storeVariables directly, no event needed
    if (globalThis.storeVariables?.geoblockingStatus !== GEOBLOCKING_STATUS.LOADING) {
      setCountryCode(globalThis.storeVariables?.countryCode || null);
      return;
    }

    // Geoblocking still loading — wait for it to finish
    function onGeoblockingStatus(event) {
      const status = event.detail?.status;
      if (status !== GEOBLOCKING_STATUS.ENABLED && status !== GEOBLOCKING_STATUS.DISABLED) return;
      const code = globalThis.storeVariables?.countryCode || null;
      if (code) setCountryCode(code);
    }
    document.addEventListener(GEOBLOCKING_STATUS_EVENT, onGeoblockingStatus);
    return () => document.removeEventListener(GEOBLOCKING_STATUS_EVENT, onGeoblockingStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once at mount to set up geoblocking listener

  return cart?.zone || resolveFromCookies() || countryCode;
}
